import { useState } from 'react';
import { format } from 'date-fns';
import { StickyNote, Plus, Trash2, Pencil, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useNoteStore } from '@/store/noteStore';
import { useToast } from '@/components/ui/use-toast';
import { AttachmentSection } from '@/components/attachments/AttachmentSection';
import type { Note } from '@/types/crm';

interface NoteListProps {
  leadId?: string;
  dealId?: string;
  notes: Note[];
}

export function NoteList({ leadId, dealId, notes }: NoteListProps) {
  const { createNote, updateNote, deleteNote } = useNoteStore();
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [expandedNote, setExpandedNote] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setIsLoading(true);
    try {
      await createNote({ lead_id: leadId, deal_id: dealId, content: newNoteContent });
      setNewNoteContent('');
      setIsAdding(false);
      toast({ title: 'Note added successfully' });
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateNote = async (id: string) => {
    if (!editingContent.trim()) return;
    setIsLoading(true);
    try {
      await updateNote(id, editingContent);
      setEditingNoteId(null);
      setEditingContent('');
      toast({ title: 'Note updated successfully' });
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (id: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;
    try {
      await deleteNote(id);
      toast({ title: 'Note deleted' });
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    }
  };

  const startEditing = (note: Note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <StickyNote className="h-4 w-4" />
          Notes ({notes.length})
        </h3>
        {!isAdding && (
          <Button size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Note
          </Button>
        )}
      </div>

      {isAdding && (
        <Card>
          <CardContent className="pt-4">
            <Textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Write your note here..."
              rows={4}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => { setIsAdding(false); setNewNoteContent(''); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddNote} disabled={isLoading || !newNoteContent.trim()}>
                <Save className="h-4 w-4 mr-1" />
                {isLoading ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {notes.length === 0 && !isAdding ? (
        <p className="text-muted-foreground text-sm py-4 text-center">No notes yet. Click "Add Note" to create one.</p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardContent className="pt-4">
                {editingNoteId === note.id ? (
                  <>
                    <Textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      rows={4}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => { setEditingNoteId(null); setEditingContent(''); }}>
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleUpdateNote(note.id)} disabled={isLoading}>
                        <Save className="h-4 w-4 mr-1" />
                        {isLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-sm whitespace-pre-wrap flex-1">{note.content}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEditing(note)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteNote(note.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                        {note.updated_at !== note.created_at && ' (edited)'}
                      </span>
                      <button
                        onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
                        className="text-xs text-primary hover:underline"
                      >
                        Attachments
                      </button>
                    </div>
                    {expandedNote === note.id && (
                      <div className="mt-3 pt-3 border-t">
                        <AttachmentSection entityType="note" entityId={note.id} compact />
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
